function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Component, createRef } from "react";
import debounce from "lodash-es/debounce";
import { jsx as _jsx } from "react/jsx-runtime";
const getDisplayName = WrappedComponent => {
  return WrappedComponent.displayName || WrappedComponent.name || "Component";
};
/*
    HOC to check component width & supply updated widths as a prop (stored interally via state)
    Ensure the wrapped component contains a reference to the element you wish to measure the width of,
    typically the root element - ensure the ref is a HTML element and not a custom class component,
    as we want to use the DOM API

    Set the ref via a callback ref
    ```js
    ref={component => (this.refToMeasure = component)}>
    ```

    or

    Set the ref via createRef
    `React.createRef()` in constructor, then setting via
    ```js
    ref={this.refToMeasure}>
    ```
*/
const measureElement = function (WrappedComponent) {
  let verbose = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  class MeasureElement extends Component {
    constructor(props) {
      super(props);
      _defineProperty(this, "wrappedComponent", /*#__PURE__*/createRef());
      _defineProperty(this, "checkAndUpdateSizingWithDebounce", void 0);
      this.state = {
        width: null,
        height: null
      };
      this.checkAndUpdateSizing = this.checkAndUpdateSizing.bind(this);
      this.checkAndUpdateSizingWithDebounce = debounce(this.checkAndUpdateSizing, 200);
    }
    componentDidMount() {
      window.addEventListener("resize", this.checkAndUpdateSizingWithDebounce);
      this.checkAndUpdateSizing();
    }
    componentDidUpdate() {
      this.checkAndUpdateSizing();
    }
    componentWillUnmount() {
      window.removeEventListener("resize", this.checkAndUpdateSizingWithDebounce);
    }
    checkAndUpdateSizing() {
      var _refToUse$current, _refToUse$current2;
      if (!this.wrappedComponent.current) {
        return;
      }
      const refToUse = this.wrappedComponent.current.refToMeasure;
      const widthFromRef = refToUse ? "current" in refToUse ? (_refToUse$current = refToUse.current) === null || _refToUse$current === void 0 ? void 0 : _refToUse$current.clientWidth : refToUse.clientWidth : undefined;
      const heightFromRef = refToUse ? "current" in refToUse ? (_refToUse$current2 = refToUse.current) === null || _refToUse$current2 === void 0 ? void 0 : _refToUse$current2.clientHeight : refToUse.clientHeight : undefined;
      const newWidth = widthFromRef || 0;
      const newHeight = heightFromRef || 0;
      if (verbose) {
        if (!widthFromRef || !heightFromRef) console.warn("measureElement was used without a ref to measure");
        // not sure if we warn on height = 0?
        if (newWidth === 0 || newHeight === 0) console.warn("measureElement has a reading of 0");
      }

      // if we haven't already set the width, or if the current width doesn't match the newly rendered width
      if (this.wrappedComponent.current && this.wrappedComponent.current.refToMeasure) {
        if (
        // if we get into a null state this has a path of setting infinitely(?)
        // !this.state.height ||
        // !this.state.width ||
        this.state.height !== newHeight || this.state.width !== newWidth) {
          this.setState({
            width: newWidth,
            height: newHeight
          });
        }
      }
    }
    render() {
      return /*#__PURE__*/_jsx(WrappedComponent, _objectSpread(_objectSpread({}, this.props), {}, {
        ref: this.wrappedComponent,
        widthFromMeasureElementHOC: this.state.width,
        heightFromMeasureElementHOC: this.state.height
      }));
    }
  }
  _defineProperty(MeasureElement, "displayName", "MeasureElement(".concat(getDisplayName(WrappedComponent), ")"));
  return MeasureElement;
};
export default measureElement;